import { useMemo } from "react";
import moment from "moment-timezone";

import { ControlOption } from "./scheduledActionTypes";
import { buildControlOptions } from "./controlCatalog";

/**
 * The browser's best-guess IANA timezone, resolved once at module load. It seeds
 * a *new* action's timezone field only; it is intentionally NOT reactive to a
 * mid-session timezone/DST change (the user overrides it per-action in the form),
 * so a process-stable constant is the honest shape rather than per-mount
 * memoization that would recompute the same value on every container mount.
 */
export const TIMEZONE_DEFAULT: string = moment.tz.guess();

/**
 * The control catalog for the scheduled-action editor, memoized so the generated
 * list is built once per consumer. Every scheduled-action container (including
 * the delete-only org drawer) needs this for `ScheduledActionList`, which is why
 * it lives in a hook rather than inside the modal.
 *
 * @param configuredControlOptions When provided and non-empty, overrides the
 *   generated catalog — the wizard supplies wizard-configured controls here. An
 *   empty override falls back to the generated catalog.
 */
export function useControlOptions(configuredControlOptions?: ControlOption[]): ControlOption[] {
  return useMemo(
    () =>
      configuredControlOptions && configuredControlOptions.length > 0
        ? configuredControlOptions
        : buildControlOptions(),
    [configuredControlOptions],
  );
}
